<template>
  <div class="container index-page">
    <Loader v-if="!loaded" :isLoading="true"></Loader>

    <template v-else>
      <div class="wrapper">
        <div class="row">
          <div class="m-auto col-12">
            <div class="main-container-header">
              <div class="main-container-header__title">
                <h2>Main Agency Analytics</h2>
              </div>
            </div>
            <div class="panel">
              <div class="panel-body">

                <div class="row">
                  <div class="col-lg-12">

                    <div class="row">
                      <div class="form-group col-lg-4 col-md-4 col-sm-12">
                        <label>Select Date Range</label>

                        <date-range-picker
                            class="d-block"
                            ref="picker"
                            :locale-data="theme.localeData"
                            :max-date="maxDate"
                            :show-week-numbers="theme.showWeekNumbers"
                            :auto-apply="theme.autoApply"
                            :show-previous-month="true"
                            :append-to-body="true"
                            :ranges="theme.ranges"
                            :control-container-class="''"
                            v-model="dateRange"
                            @update="onValueChange"
                        >
                          <template #input="picker" style="min-width: 350px;">
                            <input
                                class="form-control date-range"
                                :value="`${picker.startDate && picker.endDate ? formatDatePickerDate(picker.startDate) + ' - ' +  formatDatePickerDate(picker.endDate) : ''}`"
                            />
                          </template>
                        </date-range-picker>
                      </div>
                      <div class="form-group col-lg-4 col-md-4 col-sm-12">
                        <label>Group by</label>
                        <v-select
                            v-model="selectedRange"
                            @input="onValueChange"
                            :options="rangeData"
                            :clearable="false"
                        >
                        </v-select>
                      </div>
                      <div class="form-group col-lg-4 col-md-4 col-sm-12" v-if="subagenciesData.length > 1">
                        <label>Subagencies</label>
                        <v-select
                            v-model="selectedSubagency"
                            @input="onValueChange"
                            :options="subagenciesData"
                            :clearable="false"
                        >
                        </v-select>
                      </div>
                    </div>

                    <div class="row" v-if="isManager">
                      <div class="form-group col-2">
                        <div class="dropdown export-dropdown" ref="exportDropdown">
                          <button class="btn dropdown-toggle d-block rounded-sm" @click="toggleDropdown">
                            Export
                          </button>
                          <div class="dropdown-menu export-dropdown-menu" v-if="isDropdownOpen">
                            <div class="form-row mb-3">
                              <div class="col-lg-12">
                                <div v-for="(email, index) in recipientEmails" :key="index">
                                  <div class="form-check" :class="{ 'mb-2': index !== recipientEmails.length - 1 }">
                                    <input
                                        class="form-check-input"
                                        type="checkbox"
                                        :id="email"
                                        @change="onPassengerEmailsChange(email)"
                                        :checked="selectedRecipientEmails.includes(email)"
                                    >
                                    <label class="form-check-label" :for="email">
                                      {{ email.toLowerCase() }}
                                    </label>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div class="form-row mb-3">
                              <div class="col">
                                <input
                                    class="form-control rounded-sm"
                                    style="height: 38px"
                                    type="text"
                                    placeholder="web@example.com"
                                    v-model="emailForm.email"
                                >
                              </div>
                              <div class="col-auto">
                                <button class="btn btn-tonal-primary rounded-sm d-block"
                                        @click="addEmail"
                                        :disabled="!isEmailValid">
                                  <i class="fa fa-plus" style="font-size: 12px"></i>
                                </button>
                              </div>
                            </div>

                            <div class="form-row">
                              <div class="col">
                                <button class="btn btn-primary w-100 rounded-sm justify-content-center"
                                        @click="exportData"
                                        :disabled="selectedRecipientEmails.length === 0">
                                  Send
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="row" v-if="statsBreakdown.length">
                      <div class="col-lg-12">
                        <line-chart
                            v-if="chartData.datasets && chartData.datasets.length && !chartError"
                            :chart-data="chartData"
                            :options="chart.options">
                        </line-chart>
                        <div class="has-error" v-if="chartError"> {{ chartError }}</div>
                      </div>
                    </div>

                    <div class="row" v-if="!stats.length">
                      <div class="col-lg-12">
                        <div class="has-error">
                          No consumptions found for selected period
                        </div>
                      </div>
                    </div>

                    <div class="row mt-5" v-if="stats.length">

                      <div class="form-group col-lg-4 col-md-4 col-sm-12">
                        <label>Listing by</label>
                        <v-select
                            v-model="selectedListing"
                            @input="onValueChange"
                            :options="listingData"
                            :clearable="false"
                        >
                        </v-select>
                      </div>

                      <div class="col-lg-12">
                        <table class="table table-bordered sortable-table">
                          <thead>
                          <tr>
                            <th v-if="selectedListing.code === 'provider'">Provider</th>
                            <th v-if="selectedListing.code === 'agent'">Agent</th>
                            <th v-if="selectedListing.code === 'application'">Application</th>
                            <th v-if="selectedListing.code === 'subagency'">Subagency</th>
                            <th @click="sort('orders_created')">Created <i class="fa fa-sort" v-if="stats.length > 1"></i></th>
                            <th @click="sort('orders_issued')">Issued <i class="fa fa-sort" v-if="stats.length > 1"></i></th>
                            <th @click="sort('orders_completed')">Completed <i class="fa fa-sort" v-if="stats.length > 1"></i></th>
                            <th @click="sort('orders_expired')">Expired <i class="fa fa-sort" v-if="stats.length > 1"></i></th>
                            <th @click="sort('orders_cancelled')">Cancelled <i class="fa fa-sort" v-if="stats.length > 1"></i></th>
                            <th @click="sort('orders_voided')">Voided <i class="fa fa-sort" v-if="stats.length > 1"></i></th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr :key="index" v-for="(s, index) in sortedStats">
                            <td v-if="selectedListing.code === 'provider'">{{ providersMap[s.provider_id] }}</td>
                            <td v-if="selectedListing.code === 'agent'">{{ agentsMap[s.account_id] }}</td>
                            <td v-if="selectedListing.code === 'application'">{{ applicationsMap[s.application_id] }}</td>
                            <td v-if="selectedListing.code === 'subagency'">{{ agenciesMap[s.agency_id] }}</td>
                            <td>{{ s.orders_created }}</td>
                            <td>{{ s.orders_issued }}</td>
                            <td>{{ s.orders_completed }}</td>
                            <td>{{ s.orders_expired }}</td>
                            <td>{{ s.orders_cancelled }}</td>
                            <td>{{ s.orders_voided }}</td>
                          </tr>
                          <tr v-if="stats.length > 1 && Object.keys(totalStats).length">
                            <td>TOTAL</td>
                            <td>{{totalStats.orders_created}}</td>
                            <td>{{totalStats.orders_issued}}</td>
                            <td>{{totalStats.orders_completed}}</td>
                            <td>{{totalStats.orders_expired}}</td>
                            <td>{{totalStats.orders_cancelled}}</td>
                            <td>{{totalStats.orders_voided}}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>


                  </div>
                </div>


              </div>
            </div>
          </div>


        </div>
      </div>
    </template>
  </div>

</template>

<script>
import NavigationBar from './shared/+navigation-bar'
import DateRangePicker from 'vue2-daterange-picker'
import themeService from '@/services/theme'
import chartService from '@/services/chart'
import axios from "@/services/axios";
import format from '@/mixins/format'
import moment from 'moment';
import LineChart from './shared/+line-chart.vue'
import Loader from './shared/+loader'

//you need to import the CSS manually (in case you want to override it)
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import "vue-select/src/scss/vue-select.scss";
import helpers from "@/mixins/helpers";

export default {
  components: {
    NavigationBar,
    DateRangePicker,
    LineChart,
    Loader,
  },
  mixins: [
    format,
    helpers,
  ],
  mounted: function () {
    this.init();
  },
  methods: {
    init() {
      let subagencyStatsAllowed = this.can('front.subagency');
      if (!subagencyStatsAllowed) {
        let idx = this.listingData.findIndex(item => item.code === 'subagency')
        this.listingData.splice(idx, 1)
      }

      this.dateRange = {
        startDate: moment(moment().format('YYYY-MM-01')).toDate(),
        endDate: moment(moment().format('YYYY-MM-') + moment().daysInMonth()).toDate()
      };

      const email = localStorage.getItem('email');
      if (email) {
        this.recipientEmails.push(email);
      }

      let p1 = axios.get("/agent/account/providers")
          .then(res => {
            res.data.map(p => {
              this.providersMap[p.id] = p.agw_provider_id
            })
          })
          .catch();

      let p2 = axios.get("/agent/access/account/list")
          .then(res => {
            res.data.map(p => {
              this.agentsMap[p.id] = p.email;
            })
          })
          .catch();
      let p3 = this.loadStats();

      let p4 = axios.get("/agent/access/application/list", {headers: {'Ag-Analytics': true}})
          .then(res => {
            res.data.map(p => {
              this.applicationsMap[p.data.id] = p.data.label;
            })
          })
          .catch();

      let promises = [p1, p2, p3, p4]

      if (subagencyStatsAllowed) {
        let p5 = axios.get("/agent/access/agency/list")
            .then(res => {
              res.data.map(p => {
                this.agenciesMap[p.id] = p.name;
                if (this.$localStorage.get("role") === "manager" && this.$localStorage.get("isConsumerOwner")) {
                  this.subagenciesData.push({code: p.id, label: p.name});
                }
              })
            })
            .catch();
        promises.push(p5)
      }

      Promise.all(promises).then(() => {
        setTimeout(() => {
          this.loaded = true
        }, 200);
      })
    },
    onValueChange() {
      this.loadStats();
    },
    loadStats() {
      this.stats = [];
      this.chartData = {};

      const listing = this.selectedListing.code;
      const range = this.selectedRange.code;
      const dateStart = moment(this.dateRange.startDate).format('YYYY-MM-DD');
      const dateEnd = moment(this.dateRange.endDate).format('YYYY-MM-DD');
      const subagency = this.selectedSubagency.code;

      let p;
      let pp;

      if (dateStart) {
        p = axios.get(`/agent/stats/summary?date_start=${dateStart}&listing=${listing}${dateEnd ? '&date_end=' + dateEnd : ''}${subagency ? '&agency_id=' + subagency : ''}`)
            .then(res => {
              this.stats = res.data;
              this.calculateTotalStats();
            })
            .catch();
        pp = axios.get(`/agent/stats/breakdown?date_start=${dateStart}&range=${range}${dateEnd ? '&date_end=' + dateEnd : ''}${subagency ? '&agency_id=' + subagency : ''}`)
            .then((res) => {
              this.statsBreakdown = res.data;
              this.buildBreakdownStats();
            })
            .catch();
      }

      return Promise.all([p, pp])
    },
    buildBreakdownStats() {
      this.chartError = '';
      if (!this.statsBreakdown.length) {
        return
      }
      let chartData = [
        /*{ data: [65, 59, 80, 81], label: 'Series A' },
        { data: [22, 13, 25, 65, 52, 45, 70], label: 'Series B' },*/
      ];
      let chartLabels = [];

      // let labelType = 'day';
      let format = 'YYYY-MM-DD';
      let isWeekGroup = !!this.statsBreakdown[0].month
      let val = this.statsBreakdown[0].created_at
      if (val) {
        switch (val.split('-').length) {
          case 1:
            // labelType = 'year';
            format = 'YYYY'
            break;
          case 2:
            // labelType = 'month';
            format = 'YYYY-MM'
            break;
          case 3:
            // labelType = 'day';
            format = 'YYYY-MM-DD'
            break;
        }
      }

      // find min and max dates
      let minDate = null;
      let maxDate = null;
      this.statsBreakdown.map(item => {
        let dt = this.statsItemGetDate(item, format, isWeekGroup)
        if (minDate == null || dt.unix() <= minDate.unix()) {
          minDate = dt
        }
        if (maxDate == null || dt.unix() >= maxDate.unix()) {
          maxDate = dt
        }
      })
      let curDate = minDate;
      let maxUnix = maxDate.unix();
      let label2idxMap = {};
      let dataArr = [];

      while (curDate.unix() <= maxUnix) {
        let fmtLabel = this.statsItemFormatDate(curDate, format, isWeekGroup)
        label2idxMap[fmtLabel] = chartLabels.push(fmtLabel) - 1
        dataArr.push(0);
        if (isWeekGroup) {
          curDate = curDate.add(1, 'week');
        } else {
          switch (format) {
            case 'YYYY':
              curDate = curDate.add(1, 'year');
              break;
            case 'YYYY-MM':
              curDate = curDate.add(1, 'month')
              break;
            case 'YYYY-MM-DD':
              curDate = curDate.add(1, 'day')
              break;
          }
        }
      }
      if (dataArr.length < 2) {
        this.chartError = `Not enough data to build chart with '${this.selectedRange.code}' range. Try different date range period`;
      }
      let colors = {
        'created': '#F6C343',
        'issued': '#2C7BE5',
        'cancelled': '#E63757',
        'disrupted': '#E63757',
        'expired': '#F98586',
        'completed': '#12263F',
        'voided': '#dcdcdc',
      }
      if (!chartData.length) {
        chartData = [
          {
            data: [...dataArr],
            label: 'Created',
            fill: false,
            backgroundColor: colors['created'],
            borderColor: colors['created']
          },
          {
            data: [...dataArr],
            label: 'Issued',
            fill: false,
            backgroundColor: colors['issued'],
            borderColor: colors['issued']
          },
          {
            data: [...dataArr],
            label: 'Cancelled',
            fill: false,
            backgroundColor: colors['cancelled'],
            borderColor: colors['cancelled']
          },
          {
            data: [...dataArr],
            label: 'Disrupted',
            fill: false,
            backgroundColor: colors['disrupted'],
            borderColor: colors['disrupted']
          },
          {
            data: [...dataArr],
            label: 'Expired',
            fill: false,
            backgroundColor: colors['expired'],
            borderColor: colors['expired']
          },
          {
            data: [...dataArr],
            label: 'Completed',
            fill: false,
            backgroundColor: colors['completed'],
            borderColor: colors['completed']
          },
          {
            data: [...dataArr],
            label: 'Voided',
            fill: false,
            backgroundColor: colors['voided'],
            borderColor: colors['voided']
          },
        ];
      }
      this.statsBreakdown.map(item => {
        let fmtLabel = this.statsItemFormatDate(this.statsItemGetDate(item, format, isWeekGroup), format, isWeekGroup)
        let idx = label2idxMap[fmtLabel];
        switch (item.type) {
          case 'created':
            chartData[0].data[idx] = item.count;
            break;
          case 'issued':
            chartData[1].data[idx] = item.count;
            break;
          case 'cancelled':
            chartData[2].data[idx] = item.count;
            break;
          case 'disrupted':
            chartData[3].data[idx] = item.count;
            break;
          case 'expired':
            chartData[4].data[idx] = item.count;
            break;
          case 'completed':
            chartData[5].data[idx] = item.count;
            break;
          case 'voided':
            chartData[6].data[idx] = item.count;
            break;
        }
      })
      this.chartData = {
        labels: [...chartLabels],
        datasets: [...chartData],
      };
    },
    statsItemFormatDate(curDate, format, isWeekGroup) {
      let fmtLabel
      if (isWeekGroup) {
        fmtLabel = curDate.format('MMM DD') + ' - ' + curDate.clone().endOf('isoWeek').format('DD')
      } else {
        fmtLabel = curDate.format(format)
      }
      return fmtLabel
    },
    statsItemGetDate(item, format, isWeekGroup) {
      if (isWeekGroup) {
        let date = moment(item.year).add(item.month-1, 'months').add(item.week-1, 'weeks')
        return date.clone().startOf('isoWeek');
      } else {
        return moment(item.created_at, format)
      }
    },
    calculateTotalStats() {
      this.totalStats = {}
      if (this.stats.length) {
        Object.keys(this.stats[0]).map(key => {
          if (key.startsWith('orders')) {
            this.totalStats[key] = this.getSumByKey(this.stats, key);
          }
        })
      }
    },
    getSumByKey(arr, key){
      return arr.reduce((accumulator, current) => accumulator + Number(current[key]), 0);
    },
    sort(s) {
      if (s === this.currentSort) {
        this.currentSortDir = this.currentSortDir === 'asc' ? 'desc' : 'asc';
      }
      this.currentSort = s;
    },
    onPassengerEmailsChange(email) {
      if (this.selectedRecipientEmails.includes(email)) {
        this.selectedRecipientEmails = this.selectedRecipientEmails.filter(e => e !== email);
      } else {
        this.selectedRecipientEmails.push(email);
      }
    },
    addEmail() {
      const newEmail = this.emailForm.email.trim().toLowerCase();
      if (newEmail && !this.recipientEmails.includes(newEmail)) {
        this.recipientEmails.push(newEmail);
        this.selectedRecipientEmails.push(newEmail);
      }
      this.emailForm.email = '';
    },
    exportData() {
      let payload = {};
      payload.issuingDateFrom = moment(this.dateRange.startDate).format('YYYY-MM-DD');
      payload.issuingDateTo = moment(this.dateRange.endDate).format('YYYY-MM-DD');
      payload.recipientEmails = this.selectedRecipientEmails;
      axios.post('OrderExport', payload, {
        baseURL: process.env.VUE_APP_ROOT_API.replace(/v2\.0/, 'v1.2')
      }).then(res => {
        this.$notify({
          group: 'foo',
          type: 'success',
          text: 'Your request has been processed',
        });
        this.isDropdownOpen = false;
      }).catch(error => {
        console.error('Error fetching stats:', error);
        this.$notify({
          group: 'foo',
          type: 'error',
          text: error.message,
        });
      });
    },
    closeDropdown() {
      this.isDropdownOpen = false;
    },
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
      if (this.isDropdownOpen) {
        document.addEventListener('click', this.closeDropdownOnClickOutside);
      } else {
        document.removeEventListener('click', this.closeDropdownOnClickOutside);
      }
    },
    closeDropdownOnClickOutside(event) {
      if (this.$refs && this.$refs.exportDropdown && !this.$refs.exportDropdown.contains(event.target)) {
        this.isDropdownOpen = false;
        document.removeEventListener('click', this.closeDropdownOnClickOutside);
      }
    },
  },
  data: function () {
    return {
      loaded: false,
      chartError: '',
      providersMap: {},
      agentsLoaded: false,
      agentsMap: {},
      agenciesMap: {},
      applicationsMap: {},
      selectedListing: {code: 'provider', label: 'Provider'},
      listingData: [
        {code: 'provider', label: 'Provider'},
        {code: 'agent', label: 'Agent'},
        {code: 'application', label: 'Application'},
        {code: 'subagency', label: 'Subagency'},
      ],
      selectedRange: {code: 'week', label: 'Week'},
      rangeData: [
        {code: 'day', label: 'Day'},
        {code: 'week', label: 'Week'},
        {code: 'month', label: 'Month'},
        {code: 'year', label: 'Year'},
      ],
      dateRange: {},
      subagenciesData: [
        {code: '', label: 'All'}
      ],
      selectedSubagency: {code: '', label: 'All'},

      stats: [],
      statsBreakdown: [],
      chartData: {},
      totalStats: {},
      currentSort: 'orders_created',
      currentSortDir: 'desc',
      isDropdownOpen: false,
      recipientEmails: [],
      selectedRecipientEmails: [],
      emailForm: {
        email: '',
      }
    }
  },
  computed: {
    maxDate() {
      return new Date();
    },
    theme() {
      return themeService;
    },
    chart() {
      return chartService;
    },
    sortedStats() {
      return this.stats.sort((a,b) => {
        let modifier = 1;
        if (this.currentSortDir === 'desc') modifier = -1;
        if (a[this.currentSort] < b[this.currentSort]) return -1 * modifier;
        if (a[this.currentSort] > b[this.currentSort]) return 1 * modifier;
        return 0;
      });
    },
    isEmailValid() {
      const emailPattern = /^\S+@\S+\.\S+$/;
      return emailPattern.test(this.emailForm.email);
    },
    isManager() {
      return this.$localStorage.get("role") === "manager"
    }
  },
};
</script>

<style lang="scss">
.vs {
  &__dropdown-toggle {
    color: #202020;
    font-size: 1.2em;
    font-weight: 500;
    border-color: #e0e0e0;
    padding: 0.5rem 0.75rem;
    border-radius: 0;
    height: calc(2.75rem + 2px);
  }

  &__selected {
    margin: 0;
    padding: 0;
    border: 0;
  }

  &__search {
    margin: 0;
    padding: 0;

    &:focus {
      padding: 0;
      margin: 0;
    }
  }
}

.date-range {
  height: 40.5px !important;
  border-radius: 0 !important;
  border-color: #e0e0e0 !important;
}

.export-dropdown {
  .export-dropdown-menu {
    display: block;
    border: 1px solid #e0e0e0;
    width: 324px;
    margin: 0;
    padding: 1rem;
  }
}
</style>
